/**
 * "Get your business ready" checklist: connect a channel, verify the
 * second login identity, then the first records of each part of the
 * business (contacts, catalogue, products, vendors, team, departments,
 * quotation, order). Server steps come from /api/reports/setup-progress
 * (owner/admin only, filtered by the plan's modules); the channel and
 * identity steps from useProfileCompletion's live data. Re-reads each time
 * `hostScreen` becomes the visible screen, so a step ticks as soon as the
 * user comes back from doing it (screens stay mounted in the shell).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import type { AppIconName } from '../components/icons/AppIcon';
import { apiService } from '../services/api';
import { useAppContext, Screen } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { useProfileCompletion } from './useProfileCompletion';

export type SetupSection = 'channels' | 'account' | 'customers' | 'catalogue' | 'team' | 'calendar' | 'sales';

export interface SetupItem {
  key: string;
  section: SetupSection;
  done: boolean;
  icon: AppIconName;
  screen: Screen;
  routeParams?: Record<string, any>;
}

const SERVER_STEPS: Record<string, Omit<SetupItem, 'key' | 'done'>> = {
  businessSettings: { section: 'account', icon: 'time-outline', screen: 'business-profile' },
  contacts: { section: 'customers', icon: 'people-outline', screen: 'bulk-contact-upload' },
  catalogues: { section: 'catalogue', icon: 'albums-outline', screen: 'catalogue-create' },
  products: { section: 'catalogue', icon: 'cube-outline', screen: 'product-create' },
  vendors: { section: 'catalogue', icon: 'storefront-outline', screen: 'vendor-create' },
  team: { section: 'team', icon: 'person-add-outline', screen: 'user-setup' },
  departments: { section: 'team', icon: 'git-network-outline', screen: 'department-add' },
  assignmentRules: { section: 'team', icon: 'git-branch-outline', screen: 'assignment-rules' },
  calendars: { section: 'calendar', icon: 'calendar-outline', screen: 'calendar-create' },
  quotations: { section: 'sales', icon: 'document-text-outline', screen: 'quotation-create' },
  orders: { section: 'sales', icon: 'receipt-outline', screen: 'order-add' },
};

const CHANNEL_CODES = ['wabu', 'fbm', 'x_twitter', 'phone', 'ai'];

export const SETUP_SECTIONS: SetupSection[] = ['channels', 'account', 'customers', 'catalogue', 'team', 'calendar', 'sales'];

export function useSetupProgress(hostScreen: Screen) {
  const { currentScreen } = useAppContext();
  const businessId = useAppStore((s) => s.currentBusiness?._id);
  const { profileCompletion, isLoading: profileLoading, refresh: refreshProfile } = useProfileCompletion();
  const [server, setServer] = useState<{ canSetUp: boolean; steps: Array<{ key: string; done: boolean }> } | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!businessId) return;
    try {
      const response = await apiService.getSetupProgress();
      if (response.statusCode === 1000 && response.data) setServer(response.data);
    } catch {
      // keep the last known steps
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  const visible = currentScreen === hostScreen;
  useEffect(() => {
    if (!visible) return;
    refresh();
    refreshProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, visible]);

  const items = useMemo<SetupItem[]>(() => {
    if (!server?.canSetUp) return [];
    const services = profileCompletion?.services || [];
    const channelDone = services.some((s) => CHANNEL_CODES.includes(String(s.code).toLowerCase()) && s.status === 'completed');
    const list: SetupItem[] = [
      { key: 'channels', section: 'channels', done: channelDone, icon: 'chatbubbles-outline', screen: 'channel-onboarding-stepper' },
    ];
    const ai = services.find(s => s.code === 'ai');
    if (ai) list.push({ key: 'ai', section: 'channels', done: ai.status === 'completed', icon: 'ai-bot', screen: 'ai-setup' });
    for (const s of services) {
      if (s.code === 'identity_email' || s.code === 'identity_mobile') {
        const type = s.code === 'identity_email' ? 'email' : 'mobile';
        list.push({
          key: s.code,
          section: 'account',
          done: s.status === 'completed',
          icon: type === 'email' ? 'mail-outline' : 'call-outline',
          screen: 'add-verify-identity',
          routeParams: { identityType: type },
        });
      }
    }
    for (const step of server.steps) {
      const def = SERVER_STEPS[step.key];
      if (def) list.push({ key: step.key, done: step.done, ...def });
    }
    return list;
  }, [server, profileCompletion]);

  const doneCount = items.filter((i) => i.done).length;
  return {
    canSetUp: !!server?.canSetUp,
    loading: loading || (profileLoading && !server),
    items,
    doneCount,
    total: items.length,
    next: items.find((i) => !i.done) || null,
    refresh,
  };
}
